import { AvatarGroup, ButtonBase, Card, CardActionArea, Chip, IconButton, Typography, useTheme } from '@mui/material';
import { FragmentType, getFragmentData, graphql } from '../../gql';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { PeopleAltOutlined, Info, Link, LinkOff } from '@mui/icons-material';
import { Box, Stack } from '@mui/system';
import { CardStyles, ChipStyles } from './JourneyTile.styles';
import { OodColorVariant } from '../../types/colors';
import { PersonaAvatarSmall } from '../../persona/PersonaAvatar';
import { AvatarGroupItemStyles } from '../utils/CustomAvatarGroup';
import { JourneySatisfaction } from '../JourneySatisfaction';
import slugify from 'slugify';
import { Editor } from '../../components/wysiwyg/Editor';
import { JourneyPopover } from './JourneyPopover';
import { MainMetricInfo } from '../../journey/detail/detailView/MainMetricInfo';

type JourneyItemFragmentType = FragmentType<typeof Journey_SingleJourneyFragment>;

const MAX_NUM_OF_PERSONAS = 4;

const Journey_SingleJourneyFragment = graphql(`
	fragment Journey_SingleJourneyFragment on Journey {
		id
		createdAt
		name
		description
		color
		teams {
			id
			name
		}
		personas {
			id
			...PersonaAvatar_persona
		}
		satisfaction
		activityWarnings {
			id
		}
		mainMetric {
			metric {
				id
				unitSign
				dataSet {
					metricTemplate {
						unitSign
					}
				}
			}
		}
	}
`);

type WidgetProps = Readonly<{
	onDelete?: () => void;
	isShownOnWidget?: boolean;
}>;

type Props = Readonly<{
	journeyData: JourneyItemFragmentType;
	colorVariant: OodColorVariant;
	isShownOnWidget?: boolean;
}> &
	WidgetProps;

const JourneyTile: FC<Props> = ({ journeyData, colorVariant, isShownOnWidget = false, onDelete }) => {
	const [showEditIcon, setShowEditIcon] = useState(false);
	const [isLinkOff, setIsLinkOff] = useState(false);
	const [showLink, setShowLink] = useState(false);
	const data = getFragmentData(Journey_SingleJourneyFragment, journeyData);
	const theme = useTheme();
	const navigate = useNavigate();

	return (
		<CardActionArea
			id={'cardAction'}
			onClick={() => navigate(`/journeys/${data.id}/activity-map`)}
			onMouseEnter={() => {
				setShowEditIcon(true);
				setShowLink(true);
			}}
			onMouseLeave={() => {
				setShowEditIcon(false);
				setShowLink(false);
			}}
			data-journey={slugify(data.name, { lower: true })}
			sx={{ borderRadius: 2 }}
		>
			<Card variant={'outlined'} sx={CardStyles(colorVariant)}>
				<Stack justifyContent={'flex-start'} width={'100%'} p={2}>
					<Stack direction={'row'} justifyContent={'space-between'} minHeight={'28px'} alignItems={'center'} mb={'16px'}>
						<Stack direction={'row'} justifyContent={'flex-start'} flexWrap={'wrap'} gap={0.5}>
							{data.teams.length > 0 &&
								data.teams.map((team) => (
									<Chip
										key={team.id}
										icon={<PeopleAltOutlined sx={{ fill: colorVariant.content, fontSize: '16px' }} />}
										label={team.name}
										variant={'outlined'}
										sx={{
											...ChipStyles(colorVariant),
											marginRight: 'unset',
										}}
									/>
								))}
						</Stack>
						{isShownOnWidget && showLink && (
							<IconButton
								sx={{ position: 'absolute', right: 0, top: 0 }}
								onClick={(e) => {
									e.stopPropagation();
									onDelete != null && onDelete();
								}}
								onMouseEnter={() => setIsLinkOff(true)}
								onMouseLeave={() => setIsLinkOff(false)}
							>
								{isLinkOff ? <LinkOff /> : <Link />}
							</IconButton>
						)}

						{showEditIcon && !isShownOnWidget && (
							<Box position={'absolute'} right={0} top={0} margin={2} zIndex={1} onClick={(e) => e.stopPropagation()}>
								<JourneyPopover journeyData={data} />
							</Box>
						)}
					</Stack>
					<Stack mb={2} gap={1}>
						<Box mb={'auto'}>
							<Typography variant={'h2'} fontSize={'16px'}>
								{data.name}
							</Typography>
						</Box>
						{data.mainMetric?.metric?.id != null && (
							<MainMetricInfo
								metricId={data.mainMetric.metric.id}
								unitSign={data.mainMetric.metric.unitSign ?? data.mainMetric.metric.dataSet?.metricTemplate?.unitSign ?? null}
							/>
						)}
						<Box
							lineHeight={'20px'}
							fontSize={'12px'}
							sx={{
								'div[contenteditable=false]': {
									display: '-webkit-box',
									overflow: 'hidden',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
								},
							}}
						>
							{data.description != null && <Editor readonly state={data.description} />}
						</Box>
					</Stack>

					<Stack flexDirection={'row'} gap={isShownOnWidget ? 2 : 0} justifyContent={'space-between'} marginTop={'auto'}>
						<AvatarGroup
							max={MAX_NUM_OF_PERSONAS}
							sx={{
								alignItems: 'center',
								'.MuiAvatarGroup-avatar': AvatarGroupItemStyles(theme),
								'.MuiAvatar-root:first-of-type': { background: 'transparent', right: -5 },
							}}
						>
							{data.personas && data.personas.map((persona) => <PersonaAvatarSmall key={persona.id} persona={persona} />)}
						</AvatarGroup>

						<Stack flexDirection={'row'} alignSelf={'flex-end'} justifyContent={'center'} gap={'12px'}>
							<JourneySatisfaction satisfaction={data.satisfaction ?? null} />
							{(data.activityWarnings?.length ?? 0) > 0 && (
								<ButtonBase
									sx={{ padding: 0.75, borderRadius: 1 }}
									onClick={(e) => {
										e.stopPropagation();
										navigate(`/journeys/${data.id}/warnings`);
									}}
								>
									<Stack flexDirection={'row'} alignItems={'center'} justifyContent={'center'}>
										<Info sx={{ fill: '#FF4D4F', color: '#fff', marginRight: '4px' }} />
										<Stack flexDirection={'row'} alignItems={'cenetr'}>
											{data.activityWarnings?.length}
										</Stack>
									</Stack>
								</ButtonBase>
							)}
						</Stack>
					</Stack>
				</Stack>
			</Card>
		</CardActionArea>
	);
};

export { JourneyTile };
